/**
 * GlacierX — Immersive Landing Page Interactions
 * v3: reveal repeatable, scene active toggle, sticky pin
 */

document.addEventListener('DOMContentLoaded', () => {
  /* ====== SUPPRESS STORE-WIDE POPUPS ON THIS STANDALONE PAGE ====== */
  const storePopupSelector = [
    '[class*="ecomsend__"]',
    '[id*="ecomsend"]',
    '.react-responsive-modal-root',
    'iframe[src*="ecomsend"]',
    'iframe[title*="EcomSend"]'
  ].join(',');
  const suppressStorePopups = () => {
    document.querySelectorAll(storePopupSelector).forEach((element) => {
      element.style.setProperty('display', 'none', 'important');
      element.style.setProperty('visibility', 'hidden', 'important');
      element.style.setProperty('pointer-events', 'none', 'important');
      element.setAttribute('aria-hidden', 'true');
    });
    document.body.style.removeProperty('overflow');
  };
  suppressStorePopups();
  new MutationObserver(suppressStorePopups).observe(document.body, {
    childList: true,
    subtree: true
  });

  /* ====== NAV SCROLL ====== */
  const nav = document.getElementById('nav');
  window.addEventListener('scroll', () => {
    nav.classList.toggle('scrolled', window.scrollY > 60);
  }, { passive: true });

  /* ====== NAV MOBILE MENU ====== */
  const navToggle = document.querySelector('[data-nav-toggle]');
  const navMenu = document.querySelector('[data-nav-menu]');
  const closeNavMenu = () => {
    if (!nav || !navToggle) return;
    nav.classList.remove('nav-open');
    navToggle.setAttribute('aria-expanded', 'false');
    navToggle.setAttribute('aria-label', 'Open menu');
  };
  navToggle?.addEventListener('click', () => {
    const open = nav.classList.toggle('nav-open');
    navToggle.setAttribute('aria-expanded', String(open));
    navToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  });
  navMenu?.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => closeNavMenu());
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') closeNavMenu();
  });
  document.addEventListener('click', event => {
    if (nav?.classList.contains('nav-open') && !event.target.closest('.nav')) closeNavMenu();
  });

  /* ====== HERO LOAD SEQUENCE ====== */
  requestAnimationFrame(() => {
    document.querySelectorAll('#hero .reveal').forEach(el => el.classList.add('visible'));
  });

  /* ====== EARLY BIRD SIGNUP ====== */
  const signupForm = document.getElementById('glacierx-crowdfunding-signup');
  const signupEmail = document.getElementById('crowdfunding-signup-email');
  // The result query identifies success; the form's scrolling anchor is unused.
  const removeSignupAnchor = () => {
    const url = new URL(window.location.href);
    if (url.hash !== '#glacierx-crowdfunding-signup') return;
    url.hash = '';
    window.history.replaceState({}, '', url);
  };
  const removeSignupActionAnchor = () => {
    if (!signupForm) return;
    const action = new URL(signupForm.action, window.location.href);
    action.hash = '';
    signupForm.action = action.href;
  };
  removeSignupAnchor();
  removeSignupActionAnchor();
  const signupPendingKey = 'glacierx-crowdfunding-signup-pending';
  const signupPendingLifetime = 10 * 60 * 1000;
  document.querySelectorAll('[data-focus-signup]').forEach(link => {
    link.addEventListener('click', event => {
      event.preventDefault();
      signupEmail?.focus({ preventScroll: true });
      signupEmail?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  });

  /* ====== TAME STORE-WIDE FLOATING WIDGETS ON MOBILE ======
     店铺全局 App 会在左右下角注入固定定位的悬浮按钮，移动端会盖住底部订阅条。
     这里自动找出这些角落悬浮按钮：缩小到 58%，并上移到订阅条上方。只处理贴边、
     尺寸适中的固定元素；页面自有元素（订阅条/导航/弹窗/视频控件）一律跳过。 */
  const signupBarEl = document.getElementById('crowdfunding-signup');
  const tameFloatingWidgets = () => {
    if (!signupBarEl || window.innerWidth > 768) return;
    const barRect = signupBarEl.getBoundingClientRect();
    const lift = window.innerHeight - barRect.top + 8;
    document.querySelectorAll('body *').forEach(el => {
      if (el.closest('.crowdfunding-signup, .crowdfunding-success-modal, .nav, [data-demo-player]')) return;
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') return;
      const r = el.getBoundingClientRect();
      if (r.width < 28 || r.width > 180 || r.height < 28 || r.height > 180) return;
      if (r.bottom < barRect.top - 4) return;
      const centerX = (r.left + r.right) / 2;
      if (centerX > window.innerWidth * 0.35 && centerX < window.innerWidth * 0.65) return;
      el.classList.add('cf-widget-tamed');
      if (centerX < window.innerWidth / 2) el.classList.add('cf-widget-tamed--left');
      if (cs.bottom !== 'auto') {
        el.style.setProperty('bottom', `${lift}px`, 'important');
      }
    });
  };
  if (window.innerWidth <= 768) {
    tameFloatingWidgets();
    [2500, 6000, 12000].forEach(delay => setTimeout(tameFloatingWidgets, delay));
    let widgetTimer = null;
    const widgetObserver = new MutationObserver(() => {
      clearTimeout(widgetTimer);
      widgetTimer = setTimeout(tameFloatingWidgets, 500);
    });
    widgetObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => widgetObserver.disconnect(), 20000);
    window.addEventListener('resize', tameFloatingWidgets, { passive: true });
  }

  const signupModal = document.querySelector('[data-signup-success-modal]');
  const signupModalClose = signupModal?.querySelector('[data-signup-success-close]');
  const serverSignupAccepted = signupModal?.dataset.signupPostedSuccessfully === 'true';
  const rememberSignupSubmission = () => {
    try {
      const returnUrl = new URL(window.location.href);
      ['customer_posted', 'form_type', 'utf8', 'form_key'].forEach(key => returnUrl.searchParams.delete(key));
      returnUrl.hash = '';
      sessionStorage.setItem(signupPendingKey, JSON.stringify({
        submittedAt: Date.now(),
        returnUrl: returnUrl.pathname + returnUrl.search
      }));
    } catch (_) {}
  };
  const consumeSignupSubmission = () => {
    try {
      const stored = sessionStorage.getItem(signupPendingKey);
      sessionStorage.removeItem(signupPendingKey);
      const pending = JSON.parse(stored);
      const submittedAt = pending?.submittedAt;
      const age = Date.now() - submittedAt;
      if (!Number.isFinite(submittedAt) || age < 0 || age >= signupPendingLifetime ||
          typeof pending?.returnUrl !== 'string') return false;
      const destination = new URL(pending.returnUrl, window.location.origin);
      if (destination.origin !== window.location.origin || destination.username || destination.password) return false;
      return destination.searchParams.get('view') === 'glacierx-crowdfunding' ||
        /\/pages\/glacierx\/?$/.test(destination.pathname);
    } catch (_) {
      return false;
    }
  };
  const publishAcceptedSignup = () => {
    let attempts = 0;
    const publishWhenReady = () => {
      const analytics = window.Shopify?.analytics;
      if (typeof analytics?.publish === 'function') {
        // Retry only API availability, never an attempted publish with uncertain delivery.
        try {
          analytics.publish('glacierx_newsletter_submission_accepted', {
            cf_project: 'glacierx',
            form_id: 'glacierx-crowdfunding-signup',
            signup_location: 'floating_bar'
          });
        } catch (_) {}
        return;
      }
      if (++attempts < 20) setTimeout(publishWhenReady, 250);
    };
    publishWhenReady();
  };
  const openSignupModal = () => {
    if (!signupModal) return;
    // 确保弹窗直接挂在 body 下，避免祖先元素的 transform/filter/backdrop-filter
    // 把 position:fixed 的包含块改掉，导致弹窗跑到页面底部而不是视口正中。
    if (signupModal.parentElement !== document.body) {
      document.body.appendChild(signupModal);
    }
    signupModal.hidden = false;
    signupModal.classList.add('is-open');
    signupModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('crowdfunding-modal-open');
    signupModalClose?.focus();
  };
  const closeSignupModal = () => {
    if (!signupModal) return;
    signupModal.hidden = true;
    signupModal.classList.remove('is-open');
    signupModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('crowdfunding-modal-open');
    signupEmail?.focus({ preventScroll: true });
    // Keep the success query while the result is shown; dismissing consumes it.
    const url = new URL(window.location.href);
    url.searchParams.delete('customer_posted');
    window.history.replaceState({}, '', url);
  };

  const signupSubmitButton = signupForm?.querySelector('[data-signup-submit]');
  const setSignupLoading = (isLoading) => {
    if (!signupSubmitButton) return;
    signupSubmitButton.classList.toggle('is-loading', isLoading);
    signupSubmitButton.disabled = isLoading;
    signupSubmitButton.setAttribute('aria-busy', String(isLoading));
  };

  const signupMessage = signupForm?.querySelector('[data-signup-message]');
  let signupSubmitting = false;
  let signupNavigationTimer;
  const showSignupError = (message) => {
    if (!signupMessage) return;
    signupMessage.textContent = message;
    signupMessage.hidden = false;
  };

  // Use Shopify's supported API. Its callback means the form is wired up;
  // form.submit() then obtains a fresh token before performing the native POST.
  // Calling HTMLFormElement.prototype.submit here would bypass that protection.
  const prepareSignupCaptcha = () => new Promise((resolve, reject) => {
    let settled = false;
    let retryTimer;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutTimer);
      clearTimeout(retryTimer);
      if (error) reject(error);
      else resolve();
    };
    const timeoutTimer = setTimeout(() => finish(new Error('CAPTCHA unavailable')), 15000);
    const protect = () => {
      if (settled) return;
      if (typeof window.Shopify?.captcha?.protect !== 'function') {
        retryTimer = setTimeout(protect, 100);
        return;
      }
      try {
        window.Shopify.captcha.protect(signupForm, () => finish());
      } catch (error) {
        finish(error);
      }
    };
    protect();
  });

  // Stop popup apps from handling this submission. Shopify's protected submit
  // method still runs, including token acquisition and form-data persistence.
  document.addEventListener('submit', async (event) => {
    if (event.target !== signupForm || !signupForm) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (signupSubmitting) return;
    signupSubmitting = true;
    setSignupLoading(true);
    if (signupMessage) signupMessage.hidden = true;

    // Let Shopify generate its native success/error redirect. A fixed return_to
    // replaces that URL and can drop customer_posted, losing the result state.
    signupForm.querySelectorAll('input[name="return_to"]').forEach(input => input.remove());
    removeSignupActionAnchor();
    try {
      await prepareSignupCaptcha();
      if (!signupForm.reportValidity()) {
        signupSubmitting = false;
        setSignupLoading(false);
        return;
      }
      rememberSignupSubmission();
      // The protected method may wait asynchronously for verification. Do not
      // retry automatically: a pending request could still complete later.
      signupNavigationTimer = setTimeout(() => {
        setSignupLoading(false);
        if (signupSubmitButton) signupSubmitButton.disabled = true;
        showSignupError('Verification is taking longer than expected. Please refresh the page before trying again.');
      }, 45000);
      signupForm.submit();
    } catch (_) {
      clearTimeout(signupNavigationTimer);
      signupSubmitting = false;
      setSignupLoading(false);
      showSignupError('Unable to start verification. Please check your connection and try again.');
    }
  }, true);

  // Back/forward cache restores the disabled button as well as the document.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    clearTimeout(signupNavigationTimer);
    signupSubmitting = false;
    setSignupLoading(false);
    if (signupMessage) signupMessage.hidden = true;
  });

  /* 刷新后的补开弹窗：只认「真实成功」信号。
     注意：提交失败时 Shopify 同样会带着 form_type=customer 和 #crowdfunding-signup
     跳回本页并渲染错误信息，所以这两个条件不能再作为开弹窗依据，
     否则会出现「错误提示和成功弹窗同时出现」的问题。 */
  const signupParams = new URLSearchParams(window.location.search);
  const hadPendingSignup = consumeSignupSubmission();
  const liquidSignupSucceeded = signupModal?.classList.contains('is-open');
  const shopifySignupSucceeded = signupParams.get('customer_posted') === 'true';
  const signupHasErrors = signupMessage?.dataset.signupErrors === 'true';
  // The URL/modal can restore the UI; analytics also requires Shopify's render flag
  // and a consumed local submission. Acceptance does not imply a new subscriber.
  if (serverSignupAccepted && hadPendingSignup && !signupHasErrors) publishAcceptedSignup();
  if (!signupHasErrors && (liquidSignupSucceeded || shopifySignupSucceeded)) {
    openSignupModal();

    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('form_type');
    cleanUrl.searchParams.delete('utf8');
    cleanUrl.searchParams.delete('form_key');
    cleanUrl.hash = '';
    window.history.replaceState({}, '', cleanUrl);
  }
  signupModalClose?.addEventListener('click', closeSignupModal);
  signupModal?.addEventListener('click', event => {
    if (event.target === signupModal) closeSignupModal();
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && signupModal && !signupModal.hidden) closeSignupModal();
  });

  /* ====== REVEAL ON SCROLL (可重复触发) ====== */
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.target.closest('#hero')) {
        entry.target.classList.toggle('visible', entry.isIntersecting);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -20px 0px' });

  document.querySelectorAll('.reveal').forEach(el => {
    if (!el.closest('#hero')) revealObserver.observe(el);
  });

  /* ====== MEET GLACIER X VIDEO CONTROLS ====== */
  document.querySelectorAll('[data-demo-player]').forEach(player => {
    const video = player.querySelector('video');
    const toggle = player.querySelector('[data-demo-toggle]');
    const seek = player.querySelector('[data-demo-seek]');
    const current = player.querySelector('[data-demo-current]');
    const duration = player.querySelector('[data-demo-duration]');
    const mute = player.querySelector('[data-demo-mute]');
    const sound = player.querySelector('[data-demo-sound]');
    const speed = player.querySelector('[data-demo-speed]');
    const fullscreen = player.querySelector('[data-demo-fullscreen]');
    if (!video || !toggle || !seek || !current || !duration || !mute || !sound || !speed || !fullscreen) return;

    const formatTime = seconds => {
      if (!Number.isFinite(seconds)) return '0:00';
      const total = Math.floor(seconds);
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    };
    const updateTime = () => {
      const length = video.duration;
      const canSeek = Number.isFinite(length) && length > 0;
      seek.disabled = !canSeek;
      seek.max = canSeek ? String(length) : '100';
      seek.value = canSeek ? String(Math.min(video.currentTime, length)) : '0';
      seek.style.setProperty('--progress', canSeek ? `${video.currentTime / length * 100}%` : '0%');
      current.textContent = formatTime(video.currentTime);
      duration.textContent = formatTime(length);
    };
    const updatePlayback = () => {
      player.classList.toggle('is-paused', video.paused);
      toggle.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
    };
    const updateSound = () => {
      mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      sound.textContent = video.muted ? 'Unmute' : 'Mute';
    };
    const updateFullscreen = () => {
      const active = document.fullscreenElement === player || document.webkitFullscreenElement === player;
      player.classList.toggle('is-fullscreen', active);
      fullscreen.setAttribute('aria-label', active ? 'Exit fullscreen' : 'Enter fullscreen');
    };
    const nativeVideoFullscreen = () => {
      video.controls = true;
      try { video.webkitEnterFullscreen(); }
      catch (_) { video.controls = false; }
    };

    toggle.addEventListener('click', () => {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });
    seek.addEventListener('input', () => {
      video.currentTime = Number(seek.value);
      updateTime();
    });
    mute.addEventListener('click', () => { video.muted = !video.muted; });
    speed.addEventListener('change', () => { video.playbackRate = Number(speed.value); });
    fullscreen.addEventListener('click', () => {
      const active = document.fullscreenElement === player || document.webkitFullscreenElement === player;
      if (active) {
        if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } else if (document.fullscreenEnabled && player.requestFullscreen) {
        player.requestFullscreen().catch(() => {});
      } else if (document.webkitFullscreenEnabled && player.webkitRequestFullscreen) {
        player.webkitRequestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        nativeVideoFullscreen();
      }
    });
    video.addEventListener('play', updatePlayback);
    video.addEventListener('pause', updatePlayback);
    video.addEventListener('volumechange', updateSound);
    video.addEventListener('ratechange', () => { speed.value = String(video.playbackRate); });
    video.addEventListener('webkitendfullscreen', () => {
      video.controls = !player.classList.contains('is-ready');
      updateFullscreen();
    });
    document.addEventListener('fullscreenchange', updateFullscreen);
    document.addEventListener('webkitfullscreenchange', updateFullscreen);
    video.addEventListener('loadedmetadata', updateTime);
    video.addEventListener('durationchange', updateTime);
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('error', () => {
      player.classList.remove('is-ready');
      video.controls = true;
    });

    updateTime();
    updatePlayback();
    updateSound();
    speed.value = String(video.playbackRate);
    fullscreen.hidden = !((document.fullscreenEnabled && player.requestFullscreen) || (document.webkitFullscreenEnabled && player.webkitRequestFullscreen) || video.webkitEnterFullscreen);
    updateFullscreen();
    video.controls = false;
    player.classList.add('is-ready');
  });

  /* ====== SCENE ACTIVE TOGGLE ====== */
  const sceneObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      entry.target.classList.toggle('scene-active', entry.isIntersecting);
    });
  }, { threshold: 0.2 });

  document.querySelectorAll('.scene').forEach(scene => sceneObserver.observe(scene));

  /* ====== COUNT-UP (一次性 — 数字滚动到目标值后不再变) ====== */
  const countObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const target = parseInt(el.getAttribute('data-target'), 10);
      if (isNaN(target)) return;
      const duration = 1800;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const e = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * e).toLocaleString('en-US');
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = target.toLocaleString('en-US');
      };
      requestAnimationFrame(tick);
      countObserver.unobserve(el);
    });
  }, { threshold: 0.4 });
  document.querySelectorAll('.count-up').forEach(el => countObserver.observe(el));

  /* ====== DPI LED PREVIEW ====== */
  const dpiPreview = document.getElementById('dpiPreview');
  const dpiPreviewImage = document.getElementById('dpiPreviewImage');
  const dpiLeds = document.querySelectorAll('.dpi-led');
  const setDpiPreview = (led) => {
    if (!dpiPreview || !dpiPreviewImage) return;
    dpiPreviewImage.src = led.dataset.image;
    dpiPreviewImage.alt = led.dataset.alt;
    dpiLeds.forEach(item => {
      const active = item === led;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
  };
  dpiLeds.forEach(led => {
    led.addEventListener('mouseenter', () => setDpiPreview(led));
    led.addEventListener('focus', () => setDpiPreview(led));
    led.addEventListener('click', () => setDpiPreview(led));
  });

  /* ====== PRESET VIDEO TABS ====== */
  const presetPreview = document.getElementById('presetPreview');
  const presetVideo = document.getElementById('presetPreviewVideo');
  const presetLoading = presetPreview?.querySelector('.preset-loading');
  const presetCards = [...document.querySelectorAll('.preset-card[data-preset-video]')];
  const showPresetLoading = () => {
    if (!presetPreview) return;
    presetPreview.classList.add('is-loading');
    presetPreview.setAttribute('aria-busy', 'true');
    presetLoading?.removeAttribute('aria-hidden');
  };
  const hidePresetLoading = () => {
    if (!presetPreview) return;
    presetPreview.classList.remove('is-loading');
    presetPreview.setAttribute('aria-busy', 'false');
    presetLoading?.setAttribute('aria-hidden', 'true');
  };
  const setPresetPreview = (card) => {
    if (!presetVideo || !card) return;

    presetCards.forEach(item => {
      const active = item === card;
      item.classList.toggle('active', active);
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });

    if (presetVideo.getAttribute('src') !== card.dataset.presetVideo) {
      showPresetLoading();
      presetVideo.pause();
      presetVideo.src = card.dataset.presetVideo;
      presetVideo.setAttribute('aria-label', `${card.dataset.presetName} preset demonstration`);
      presetVideo.load();
      presetVideo.play().catch(() => {});
    }
  };

  if (presetVideo) {
    ['loadstart', 'waiting'].forEach(eventName => {
      presetVideo.addEventListener(eventName, showPresetLoading);
    });
    ['canplay', 'playing'].forEach(eventName => {
      presetVideo.addEventListener(eventName, hidePresetLoading);
    });
    presetVideo.addEventListener('error', hidePresetLoading);
    if (presetVideo.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) hidePresetLoading();
    else showPresetLoading();
  }

  presetCards.forEach((card, index) => {
    card.addEventListener('click', () => setPresetPreview(card));
    card.addEventListener('keydown', (event) => {
      let nextIndex = index;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % presetCards.length;
      else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + presetCards.length) % presetCards.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = presetCards.length - 1;
      else return;

      event.preventDefault();
      setPresetPreview(presetCards[nextIndex]);
      presetCards[nextIndex].focus();
    });
  });

  /* ====== PARALLAX ON HERO KV ====== */
  const heroVisual = document.querySelector('.kv-mouse');
  const heroSection = document.getElementById('hero');
  if (heroVisual && heroSection) {
    heroSection.addEventListener('mousemove', (e) => {
      const r = heroSection.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const x = (e.clientX - r.left - r.width / 2) / r.width * 18;
      const y = (e.clientY - r.top - r.height / 2) / r.height * 18;
      heroVisual.style.transform = `translate3d(${x}px,${y}px,0)`;
    }, { passive: true });
    heroSection.addEventListener('mouseleave', () => {
      heroVisual.style.transform = '';
    });
  }

  /* ====== ACTIVE NAV HIGHLIGHT ====== */
  const navLinks = document.querySelectorAll('.nav-links a');
  const sections = [...navLinks].map(a => document.querySelector(a.getAttribute('href'))).filter(Boolean);
  window.addEventListener('scroll', () => {
    const y = window.scrollY + 120;
    let current = '';
    sections.forEach((sec, i) => {
      if (sec && sec.offsetTop <= y) current = navLinks[i]?.getAttribute('href');
    });
    navLinks.forEach(a => {
      a.style.opacity = a.getAttribute('href') === current ? '1' : '';
    });
  }, { passive: true });
});
